/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import axios from "axios";
import baseUrl from "..";
import { authToken, authUserID, axiosInstance } from "../axiosConfig";

// Fetches sterility test list
const getSterilityTestList = async (page: number, size: number, sort: any, order: any, testMethodeId?: any, status?: string, fromDate?: string, toDate?: string) => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/sterility_test/list?page=${page}&size=${size}&sort=${sort}&order=${order}&test_method_id=${testMethodeId}&status=${status}&from_date=${fromDate}&to_date=${toDate}`,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
  }
};

// Fetches sterility test stat
const getSterilityTestStats = async () => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/sterility_test/stats`,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
  }
};

const scanMediaTubeWorkflow = async (dataToSend: any) => {
    try {
      const headers = {
        'x-auth-token': authToken,
        'x-userid': authUserID,
      };
      const response = await axiosInstance.post(`${baseUrl}/scan_media_tube/scan_media_tube_workflow`, dataToSend, { headers });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response?.data
      }
    }
  }

  const sterilityTestStart = async (dataToSend: any) => {
    try {
      const headers = {
        'x-auth-token': authToken,
        'x-userid': authUserID,
      };
      const response = await axiosInstance.post(`${baseUrl}/sterility_test/start`, dataToSend, { headers });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response?.data
      }
    }
  }

  // Fetches sterility test lab approval stats
  const getSterilityTestLabApprovalStats = async () => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/sterility_test_lab_approval/stats`,
        {
          headers: {
            "x-auth-token": authToken,
            "x-userid": authUserID,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response?.data;
      }
    }
  };

  // Fetches sterility test lab approval list
  const getSterilityTestLabApprovalList = async (page: number, size: number, sort: string, order: string, approvalStatus: string, mediaName?: string,testMethod?: string, keyword?: string, sterilityTestId?: string) => {
    try {
      let url = `${baseUrl}/sterility_test_lab_approval/list?page=${page}&size=${size}&sort=${sort}&order=${order}&approval_status=${approvalStatus}`;
      if (mediaName) url += `&media_name_id=${mediaName}`;
      if (testMethod) url += `&test_method=${testMethod}`;
      if (keyword) url += `&keyword=${keyword}`;
      if (sterilityTestId) url += `&sterility_test_id=${sterilityTestId}`;

      const response = await axiosInstance.get(
        url,
        {
          headers: {
            "x-auth-token": authToken,
            "x-userid": authUserID,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response?.data;
      }
    }
  };

  // Fetches sterility test lab approval view details
  const getSterilityTestLabApprovalView = async (sterilityTestId: string) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/sterility_test_lab_approval/view?sterility_test_id=${sterilityTestId}`,
        {
          headers: {
            "x-auth-token": authToken,
            "x-userid": authUserID,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response?.data;
      }
    }
  };

  // Approve or reject sterility test lab approval
  const approveSterilityTestLabApproval = async (userId: string, sterilityTestId: string | number, labTestStatus: number, approverRemarks: string) => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}/sterility_test_lab_approval/approve`,
        {
          user_id: userId,
          sterility_test_id: sterilityTestId,
          lab_test_status: labTestStatus,
          approver_remarks: approverRemarks
        },
        {
          headers: {
            "x-auth-token": authToken,
            "x-userid": authUserID,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response?.data;
      }
    }
  };

  // Fetch media masters list
  const getMediaMastersList = async (sourceType: string = 'INH') => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/media/list_media_masters?sort=id&order=desc&source_type=${sourceType}`,
        {
          headers: {
            "x-auth-token": authToken,
            "x-userid": authUserID,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response?.data;
      }
    }
  };

  // Fetch sterility test identifiers list
  const getSterilityTestIdentifiersList = async () => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/sterility_test_identifiers/list?sort=id&order=desc`,
        {
          headers: {
            "x-auth-token": authToken,
            "x-userid": authUserID,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response?.data;
      }
    }
  };

  // Fetch sterility autoclave list
  const getSterilityAutoclaveList = async (page: number, size: number, sort: string, order: string, status: string, areaMasterId?: string, tubeId?: string, serialNumber?: string, batchNumber?: string, lotId?: string, lotCode?: string, sterilityTestId?: string, sterilityTestIdentifier?: string, fromDate?: string, toDate?: string, keyword?: string, scheduleProductionId?: string, productId?: string) => {
    try {
      let url = `${baseUrl}/sterility_autoclave/list?page=${page}&size=${size}&sort=${sort}&order=${order}&status=${status}`;
      if (areaMasterId !== undefined && areaMasterId !== "") url += `&area_master_id=${areaMasterId}`;
      if (tubeId !== undefined && tubeId !== "") url += `&tube_id=${tubeId}`;
      if (serialNumber !== undefined && serialNumber !== "") url += `&serial_number=${serialNumber}`;
      if (batchNumber !== undefined && batchNumber !== "") url += `&batch_number=${batchNumber}`;
      if (lotId !== undefined && lotId !== "") url += `&lot_id=${lotId}`;
      if (lotCode !== undefined && lotCode !== "") url += `&lot_code=${lotCode}`;
      if (sterilityTestId !== undefined && sterilityTestId !== "") url += `&sterility_test_id=${sterilityTestId}`;
      if (sterilityTestIdentifier !== undefined && sterilityTestIdentifier !== "") url += `&sterility_test_identifier=${sterilityTestIdentifier}`;
      if (fromDate !== undefined && fromDate !== "") url += `&from_date=${fromDate}`;
      if (toDate !== undefined && toDate !== "") url += `&to_date=${toDate}`;
      if (keyword !== undefined && keyword !== "") url += `&keyword=${keyword}`;
      if (scheduleProductionId !== undefined && scheduleProductionId !== "") url += `&schedule_production_id=${scheduleProductionId}`;
      if (productId !== undefined && productId !== "") url += `&product_id=${productId}`;

      const response = await axiosInstance.get(
        url,
        {
          headers: {
            "x-auth-token": authToken,
            "x-userid": authUserID,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response?.data;
      }
    }
  };

  // Fetch media tubes ready for autoclaving
  const getReadyForAutoclaveList = async (
    page: number,
    size: number,
    sort: string,
    order: string,
    status?: string,
    areaMasterId?: string,
    tubeId?: string,
    serialNumber?: string,
    batchNumber?: string,
    lotId?: string,
    lotCode?: string,
    sterilityTestId?: string,
    sterilityTestIdentifier?: string,
    fromDate?: string,
    toDate?: string,
    keyword?: string,
    scheduleProductionId?: string,
    productId?: string
  ) => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        size: String(size),
        sort,
        order,
        status: status ?? "",
        // area_master_id: areaMasterId ?? "",
        assigned_autoclave: areaMasterId ?? "",
        tube_id: tubeId ?? "",
        serial_number: serialNumber ?? "",
        batch_number: batchNumber ?? "",
        lot_id: lotId ?? "",
        lot_code: lotCode ?? "",
        sterility_test_id: sterilityTestId ?? "",
        sterility_test_identifier: sterilityTestIdentifier ?? "",
        from_date: fromDate ?? "",
        to_date: toDate ?? "",
        keyword: keyword ?? "",
        schedule_production_id: scheduleProductionId ?? "",
        product_id: productId ?? "",
      });
      const response = await axiosInstance.get(
        `${baseUrl}/sterility_autoclave/list/media_tube/ready_for_autoclave?${params.toString()}`,
        {
          headers: {
            "x-auth-token": authToken,
            "x-userid": authUserID,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response?.data;
      }
    }
  };

  // Start sterility autoclave
  const startSterilityAutoclave = async (dataToSend: any) => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}/sterility_autoclave/start`,
        dataToSend,
        {
          headers: {
            "x-auth-token": authToken,
            "x-userid": authUserID,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response?.data;
      }
    }
  };

  // Stop sterility autoclave
  const stopSterilityAutoclave = async (dataToSend: any) => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}/sterility_autoclave/stop`,
        dataToSend,
        {
          headers: {
            "x-auth-token": authToken,
            "x-userid": authUserID,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response?.data;
      }
    }
  };
// Fetches sterility observation list
const getSterilityObservationList = async (page: number, size: number, sort: any, order: any, sterilityTestId:string, observationStatus?:string, fromDate?:any, toDate?:any, recordedBy?:any, testMethodeId?:any) => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/sterility_observation/list/?page=${page}&size=${size}&sort=${sort}&order=${order}&sterility_test_id=${sterilityTestId}&schedule_status=${observationStatus || ""}&from_date=${fromDate || ""}&to_date=${toDate || ""}&recorded_by=${recordedBy || ""}&test_method_id=${testMethodeId || ""}`,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
  }
};

// Fetches sterility observation stat
const getSterilityObservationStats = async () => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/sterility_observation/stats`,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
  }
};

// save observation
const saveSterilityObservation = async (dataToSend: any) => {
  try {
    const headers = {
      'x-auth-token': authToken,
      'x-userid': authUserID,
    };
    const response = await axiosInstance.post(`${baseUrl}/sterility_observation/save/`, dataToSend, { headers });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data
    }
  }
}

// Fetches sterility test ids
const getSterilityTestIds = async () => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/sterility_test_identifiers/list`,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
  }
};


export const SterilityTestServices = {
  getSterilityTestList,
  scanMediaTubeWorkflow,
  sterilityTestStart,
  getSterilityTestStats,
  getSterilityTestLabApprovalStats,
  getSterilityTestLabApprovalList,
  getSterilityTestLabApprovalView,
  approveSterilityTestLabApproval,
  getMediaMastersList,
  getSterilityTestIdentifiersList,
  getSterilityAutoclaveList,
  getReadyForAutoclaveList,
  startSterilityAutoclave,
  stopSterilityAutoclave,
  getSterilityObservationList,
  getSterilityObservationStats,
  saveSterilityObservation,
  getSterilityTestIds
};